import { assetsClient } from '@/clients/assets-client.js';
import { toastService } from '@/services/toast.js';
import { HatElement } from '@/components/hat-element.js';

const NANO_ID_PATH_PREFIX = '/r/';

class HatAssetLinkForm extends HatElement {
    connectedCallback() {
    super.connectedCallback();
    this._renderShell();
        this._bindEvents();
    }

    _renderShell() {
        this.root.innerHTML = `
            <style>
                .card {
                    background: white;
                    border-radius: 16px;
                    padding: 24px;
                    box-shadow: 0 6px 20px rgba(0,0,0,0.08);
                    display: flex;
                    flex-direction: column;
                    gap: 20px;
                    max-width: 480px;
                }

                h2 { margin: 0; font-size: 18px; }

                .fields {
                    display: grid;
                    grid-template-columns: max-content 1fr;
                    gap: 10px 12px;
                    align-items: center;
                }

                .field { display: contents; }

                .field label {
                    font-size: 13px;
                    color: #555;
                    text-align: right;
                    white-space: nowrap;
                }

                input {
                    padding: 10px;
                    border: 1px solid #ddd;
                    border-radius: 8px;
                    font-size: 14px;
                    box-sizing: border-box;
                }

                .qr-btn {
                    padding: 10px;
                    border: 1px solid #ddd;
                    border-radius: 8px;
                    background: #f5f5f5;
                    cursor: pointer;
                    font-size: 13px;
                    white-space: nowrap;
                }

                .qr-btn:hover { background: #ececec; }

                .nano-row {
                    display: flex;
                    gap: 8px;
                    align-items: center;
                }

                .nano-row input { flex: 1; }

                .qr-container {
                    display: none;
                    border-radius: 12px;
                    overflow: hidden;
                }

                .qr-container.open { display: block; }

                .actions {
                    display: flex;
                    justify-content: flex-end;
                    gap: 10px;
                }

                .save {
                    background: #111;
                    color: white;
                    border: none;
                    padding: 10px 20px;
                    border-radius: 10px;
                    cursor: pointer;
                    font-size: 14px;
                }

                .save:disabled { opacity: 0.5; cursor: not-allowed; }

                .cancel {
                    background: none;
                    border: 1px solid #ddd;
                    padding: 10px 20px;
                    border-radius: 10px;
                    cursor: pointer;
                    font-size: 14px;
                }
            </style>

            <div class="card">
                <h2>Associate Asset Link</h2>

                <div class="fields">
                    <div class="field">
                        <label>Asset ID</label>
                        <input name="assetId" autocomplete="off" placeholder="e.g. abc123" required />
                    </div>
                    <div class="field">
                        <label>Nano ID</label>
                        <div class="nano-row">
                            <input name="nanoId" autocomplete="off" placeholder="e.g. Xk9mP2" required />
                            <button class="qr-btn" type="button">Scan QR</button>
                        </div>
                    </div>
                </div>

                <div class="qr-container">
                    <hat-qr-reader></hat-qr-reader>
                </div>

                <div class="actions">
                    <button class="cancel" type="button">Cancel</button>
                    <button class="save" type="button">Save</button>
                </div>
            </div>
        `;
    }

    _bindEvents() {
        const assetIdInput = this.root.querySelector<HTMLInputElement>('[name="assetId"]')!;
        const nanoIdInput  = this.root.querySelector<HTMLInputElement>('[name="nanoId"]')!;
        const qrBtn        = this.root.querySelector<HTMLButtonElement>('.qr-btn')!;
        const qrContainer  = this.root.querySelector<HTMLElement>('.qr-container')!;
        const qrReader     = this.root.querySelector<HTMLElement>('hat-qr-reader')!;
        const saveBtn      = this.root.querySelector<HTMLButtonElement>('.save')!;
        const cancelBtn    = this.root.querySelector<HTMLButtonElement>('.cancel')!;

        qrBtn.addEventListener('click', () => {
            const isOpen = qrContainer.classList.toggle('open');
            qrBtn.textContent = isOpen ? 'Close Scanner' : 'Scan QR';
        });

        qrReader.addEventListener('qr-detected', (e: Event) => {
            const raw: string = (e as CustomEvent).detail.value;
            const nanoId = this._extractNanoId(raw);
            if (nanoId) {
                nanoIdInput.value = nanoId;
                qrContainer.classList.remove('open');
                qrBtn.textContent = 'Scan QR';
            }
        });

        saveBtn.addEventListener('click', async () => {
            const assetId = assetIdInput.value.trim();
            const nanoId  = nanoIdInput.value.trim();

            if (!assetId || !nanoId) {
                toastService.show('Both Asset ID and Nano ID are required.', 'error');
                return;
            }

            saveBtn.disabled = true;
            try {
                await assetsClient.upsertAssetLink(assetId, nanoId);
                toastService.show('Asset link saved.', 'success');
                this.dispatchEvent(new CustomEvent('saved', {
                    detail: { assetId, nanoId },
                    bubbles: true,
                    composed: true,
                }));
                assetIdInput.value = '';
                nanoIdInput.value  = '';
            } catch (err) {
                console.error(err);
                toastService.show('Failed to save asset link.', 'error');
            } finally {
                saveBtn.disabled = false;
            }
        });

        cancelBtn.addEventListener('click', () => {
            this.dispatchEvent(new CustomEvent('cancelled', { bubbles: true, composed: true }));
        });
    }

    _extractNanoId(raw: string): string | null {
        try {
            const url = new URL(raw);
            const path = url.pathname;
            if (path.startsWith(NANO_ID_PATH_PREFIX)) {
                return path.slice(NANO_ID_PATH_PREFIX.length) || null;
            }
        } catch {
            // not a URL — ignore
        }
        return null;
    }
}

customElements.define('hat-asset-link-form', HatAssetLinkForm);